'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import { 
  TrendingUp, TrendingDown, AlertTriangle, Loader2
} from 'lucide-react';

import { 
  fetchDashboardSummary, 
  formatCurrency, 
  formatPercent,
  formatDate,
  type DashboardSummary 
} from '@/lib/api-client';

const COLORS = ['#3B82F6', '#22C55E', '#8B5CF6', '#F59E0B', '#EF4444'];

// Card Component
const Card = ({ children, className = "", isDark = true }: { children: React.ReactNode; className?: string; isDark?: boolean }) => (
  <div className={`backdrop-blur-md border rounded-xl p-5 transition-colors duration-300 ${
    isDark 
      ? 'bg-neutral-900/50 border-neutral-800' 
      : 'bg-white border-gray-200 shadow-sm'
  } ${className}`}>
    {children}
  </div>
);

const Badge = ({ type, text, isDark = true }: { type: string; text: string; isDark?: boolean }) => {
  const darkStyles: Record<string, string> = {
    success: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    warning: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    danger: "bg-rose-500/10 text-rose-400 border-rose-500/20",
    neutral: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  };
  
  const lightStyles: Record<string, string> = {
    success: "bg-emerald-100 text-emerald-700 border-emerald-300",
    warning: "bg-amber-100 text-amber-700 border-amber-300",
    danger: "bg-rose-100 text-rose-700 border-rose-300",
    neutral: "bg-blue-100 text-blue-700 border-blue-300",
  };
  
  const styles = isDark ? darkStyles : lightStyles;
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border transition-colors duration-300 ${styles[type] || styles.neutral}`}>
      {text}
    </span>
  );
};

const StatCard = ({ title, value, subtext, trend, trendValue, target, chartData, isDark = true }: any) => {
  const isPositive = trend === 'up';
  const statusColor = isPositive ? 'text-emerald-400' : 'text-rose-400';
  
  return (
    <Card isDark={isDark} className="flex flex-col justify-between group hover:shadow-lg transition-all duration-300">
      <div className="flex justify-between items-start mb-2">
        <div>
          <p className={`text-sm font-medium transition-colors duration-300 ${isDark ? 'text-neutral-400' : 'text-gray-600'}`}>{title}</p>
          <h3 className={`text-2xl font-bold mt-1 transition-colors duration-300 ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</h3>
        </div>
        {trendValue && (
          <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-colors duration-300 ${
            isDark ? 'bg-neutral-800' : 'bg-gray-100'
          } ${statusColor}`}>
            {isPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            <span>{trendValue}</span>
          </div>
        )}
      </div>
      
      {chartData && chartData.length > 0 && (
        <div className="h-10 w-full mt-2 opacity-50 group-hover:opacity-100 transition-opacity">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <Line type="monotone" dataKey="value" stroke={isPositive ? '#34D399' : '#F87171'} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      
      <div className={`mt-3 pt-3 border-t flex justify-between items-center text-xs transition-colors duration-300 ${
        isDark ? 'border-neutral-800' : 'border-gray-200'
      }`}>
        <span className={isDark ? 'text-neutral-500' : 'text-gray-500'}>{subtext}</span>
        {target && <span className={isDark ? 'text-neutral-400' : 'text-gray-600'}>Target: <span className={isDark ? 'text-white' : 'text-gray-900'}>{target}</span></span>}
      </div>
    </Card>
  );
};

export default function DashboardOverview() {
  const [timeRange, setTimeRange] = useState('Weekly');
  const [isDarkMode] = useState(true); // Will be managed by layout context in future
  
  const [dashboardData, setDashboardData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load real data on mount and when timeRange changes
  useEffect(() => {
    async function loadDashboardData() {
      try {
        setLoading(true);
        setError(null);
        
        const period = timeRange.toLowerCase() as 'daily' | 'weekly' | 'monthly';
        const days = period === 'daily' ? 30 : period === 'weekly' ? 12 : 12;
        
        const data = await fetchDashboardSummary(period, days);
        setDashboardData(data);
        console.log(`✅ Real data loaded (${period}):`, data);
      } catch (err) {
        console.error('❌ Failed to load dashboard data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    }
    
    loadDashboardData();
  }, [timeRange]);

  const revenueMixData = useMemo(() => {
    if (!dashboardData?.businessUnits) return [];
    
    return dashboardData.businessUnits.map(bu => ({
      name: bu.business_unit,
      value: Number(bu.pct_of_total_revenue) || 0,
      revenue: bu.revenue
    }));
  }, [dashboardData]);

  const trendChartData = useMemo(() => {
    if (!dashboardData?.trend) return [];
    
    return dashboardData.trend.map(day => ({
      name: formatDate(day.report_date),
      value: day.total_revenue,
      cost: day.total_cost
    }));
  }, [dashboardData]);

  const getBusinessUnit = (name: string) => {
    return dashboardData?.businessUnits.find(bu => bu.business_unit === name);
  };

  const freightData = getBusinessUnit('Freight');
  const pickPackData = getBusinessUnit('Pick & Pack');
  const storageData = getBusinessUnit('Warehouse Operations');

  return (
    <div className="p-6 space-y-6">
      
      {/* Page Header with Period Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Dashboard Overview
          </h1>
          <p className={`text-sm ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
            {dashboardData ? 'Real-time business metrics and KPIs' : 'Loading...'}
          </p>
        </div>
        
        <div className={`flex rounded-lg p-1 border ${
          isDarkMode 
            ? 'bg-neutral-900 border-neutral-800' 
            : 'bg-gray-100 border-gray-300'
        }`}>
          {['Daily', 'Weekly', 'Monthly'].map((t) => (
            <button
              key={t}
              onClick={() => setTimeRange(t)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                timeRange === t 
                  ? isDarkMode
                    ? 'bg-neutral-800 text-white shadow-sm'
                    : 'bg-white text-gray-900 shadow-sm'
                  : isDarkMode
                  ? 'text-neutral-500 hover:text-neutral-300'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <Card isDark={isDarkMode} className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-blue-500" />
            <p className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Loading Real Data from API...
            </p>
            <p className={`text-sm mt-2 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
              Fetching $7.79M revenue data
            </p>
          </div>
        </Card>
      )}

      {/* Error State */}
      {error && !loading && (
        <Card isDark={isDarkMode} className="border-rose-500/50 bg-rose-500/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-rose-500 mt-0.5 shrink-0" size={20} />
            <div>
              <h3 className="font-semibold text-rose-400 mb-1">Failed to Load Data</h3>
              <p className={`text-sm ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                {error}
              </p>
              <p className={`text-xs mt-2 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                Make sure the API server is running: <code className="px-2 py-0.5 bg-neutral-800 rounded">python3 backend/enhanced_api.py</code>
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Dashboard Content - Only show when data is loaded */}
      {!loading && dashboardData && (
        <>
          {/* Executive Summary */}
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Total Revenue" 
              value={formatCurrency(dashboardData.totals.revenue)}
              trend="up" 
              trendValue={`${formatPercent(dashboardData.totals.marginPct, 1)} margin`}
              subtext="All business units" 
              target={formatCurrency(dashboardData.totals.revenue * 0.95)}
              chartData={trendChartData.slice(-7)}
              isDark={isDarkMode}
            />
            <StatCard 
              title="Total Costs" 
              value={formatCurrency(dashboardData.totals.cost)}
              trend="down" 
              trendValue="Freight complete"
              subtext="Labor costs missing" 
              target={formatCurrency(dashboardData.totals.cost * 1.05)}
              chartData={trendChartData.slice(-7).map(d => ({ name: d.name, value: d.cost }))}
              isDark={isDarkMode}
            />
            <StatCard 
              title="Gross Margin" 
              value={formatCurrency(dashboardData.totals.margin)}
              trend="up" 
              trendValue={`${formatPercent(dashboardData.totals.marginPct, 1)}`}
              subtext="Overstated (no labor)" 
              target={formatCurrency(dashboardData.totals.margin * 0.9)}
              chartData={trendChartData.slice(-7).map(d => ({ name: d.name, value: d.value - d.cost }))}
              isDark={isDarkMode}
            />
            <Card isDark={isDarkMode} className="flex flex-col justify-center items-center relative overflow-hidden">
              <div className={`absolute top-0 left-0 w-full h-1 ${
                dashboardData.totals.marginPct >= 40 
                  ? 'bg-gradient-to-r from-emerald-500 to-green-500'
                  : 'bg-gradient-to-r from-amber-500 to-rose-500'
              }`}></div>
              <span className={`text-sm font-medium mb-1 transition-colors duration-300 ${
                isDarkMode ? 'text-neutral-400' : 'text-gray-600'
              }`}>Overall Margin %</span>
              <div className={`text-4xl font-bold mb-1 ${
                dashboardData.totals.marginPct >= 40 ? 'text-emerald-400' : 'text-amber-400'
              }`}>
                {formatPercent(dashboardData.totals.marginPct, 1)}
              </div>
              <Badge 
                type={dashboardData.totals.marginPct >= 40 ? "success" : "warning"} 
                text={dashboardData.totals.marginPct >= 40 ? "Above Target" : "Below Target (40%)"} 
                isDark={isDarkMode} 
              />
              <p className={`text-xs mt-4 text-center transition-colors duration-300 ${
                isDarkMode ? 'text-neutral-500' : 'text-gray-500'
              }`}>
                {dashboardData.totals.marginPct >= 40 
                  ? 'Strong performance'
                  : 'Missing labor costs inflate margin'}
              </p>
            </Card>
          </section>

          {/* Detailed Operations View */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Main Chart Area */}
            <Card isDark={isDarkMode} className="lg:col-span-2 h-[400px]">
              <div className="flex justify-between items-center mb-6">
                <h3 className={`text-lg font-semibold transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>30-Day Revenue & Cost Trend</h3>
                <Badge type="success" text="Real Data" isDark={isDarkMode} />
              </div>
              <ResponsiveContainer width="100%" height="85%">
                <AreaChart data={trendChartData}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F43F5E" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#F43F5E" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#262626' : '#E5E7EB'} vertical={false} />
                  <XAxis dataKey="name" stroke={isDarkMode ? '#525252' : '#9CA3AF'} tick={{fontSize: 12}} />
                  <YAxis stroke={isDarkMode ? '#525252' : '#9CA3AF'} tick={{fontSize: 12}} tickFormatter={(val: any) => `$${(val/1000).toFixed(0)}k`} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: isDarkMode ? '#171717' : '#FFFFFF', 
                      borderColor: isDarkMode ? '#262626' : '#E5E7EB', 
                      color: isDarkMode ? '#fff' : '#000'
                    }}
                    itemStyle={{color: isDarkMode ? '#fff' : '#000'}}
                    formatter={(value: any) => formatCurrency(value)}
                  />
                  <Area type="monotone" dataKey="value" stroke="#3B82F6" fillOpacity={1} fill="url(#colorValue)" name="Revenue" />
                  <Area type="monotone" dataKey="cost" stroke="#F43F5E" fillOpacity={1} fill="url(#colorCost)" name="Cost" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            {/* Revenue Mix Donut */}
            <Card isDark={isDarkMode} className="flex flex-col">
              <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Revenue Mix</h3>
              <div className="flex-1 min-h-[200px] relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={revenueMixData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {revenueMixData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: isDarkMode ? '#171717' : '#FFFFFF', 
                        borderColor: isDarkMode ? '#262626' : '#E5E7EB', 
                        borderRadius: '8px'
                      }}
                      formatter={(value: any, name: any, props: any) => [
                        `${formatPercent(value, 1)} (${formatCurrency(props.payload.revenue)})`,
                        name
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <span className={`text-xs block transition-colors duration-300 ${
                      isDarkMode ? 'text-neutral-500' : 'text-gray-500'
                    }`}>Total</span>
                    <span className={`text-xl font-bold block transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>{formatCurrency(dashboardData.totals.revenue)}</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {revenueMixData.map((item, idx) => (
                  <div key={item.name} className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[idx] }}></div>
                      <span className={`transition-colors duration-300 ${
                        isDarkMode ? 'text-neutral-400' : 'text-gray-600'
                      }`}>{item.name}</span>
                    </div>
                    <span className={`font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>{formatPercent(item.value, 1)}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Business Unit Details */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Freight */}
            {freightData && (
              <Card isDark={isDarkMode}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    🚛 Freight
                  </h3>
                  <Badge type="success" text="100% Complete" isDark={isDarkMode} />
                </div>
                <div className="space-y-3">
                  <div className={`flex justify-between border-b pb-2 ${isDarkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                    <span className={`text-sm ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>Revenue</span>
                    <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(freightData.revenue)}</span>
                  </div>
                  <div className={`flex justify-between border-b pb-2 ${isDarkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                    <span className={`text-sm ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>Costs</span>
                    <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(freightData.cost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>Margin</span>
                    <span className="text-emerald-400 font-bold">{formatPercent(freightData.margin_pct, 1)}</span>
                  </div>
                </div>
              </Card>
            )}

            {/* Pick & Pack */}
            {pickPackData && (
              <Card isDark={isDarkMode}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    📦 Pick & Pack
                  </h3>
                  <Badge type="warning" text="Labor Missing" isDark={isDarkMode} />
                </div>
                <div className="space-y-3">
                  <div className={`flex justify-between border-b pb-2 ${isDarkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                    <span className={`text-sm ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>Revenue</span>
                    <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(pickPackData.revenue)}</span>
                  </div>
                  <div className={`flex justify-between border-b pb-2 ${isDarkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                    <span className={`text-sm ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>Labor Costs</span>
                    <span className="text-amber-400 font-bold">Missing</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>Margin</span>
                    <span className="text-amber-400 font-bold">{formatPercent(pickPackData.margin_pct, 1)}</span>
                  </div>
                </div>
              </Card>
            )}

            {/* Warehouse Operations */}
            {storageData && (
              <Card isDark={isDarkMode}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    🏭 Warehouse Ops
                  </h3>
                  <Badge type="warning" text="Labor Missing" isDark={isDarkMode} />
                </div>
                <div className="space-y-3">
                  <div className={`flex justify-between border-b pb-2 ${isDarkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                    <span className={`text-sm ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>Revenue</span>
                    <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(storageData.revenue)}</span>
                  </div>
                  <div className={`flex justify-between border-b pb-2 ${isDarkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                    <span className={`text-sm ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>Costs</span>
                    <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(storageData.cost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>Margin</span>
                    <span className="text-emerald-400 font-bold">{formatPercent(storageData.margin_pct, 1)}</span>
                  </div>
                </div>
              </Card>
            )}
          </div>

        </>
      )}
    </div>
  );
}
